import React, { useEffect, useRef, useState, useCallback } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useSelector } from "react-redux"
import {
  FiCheckCircle, FiXCircle, FiClock, FiCode, FiFileText,
  FiAlertCircle, FiArrowLeft, FiAward, FiChevronRight,
} from "react-icons/fi"
import { RiSparklingLine } from "react-icons/ri"

import {
  checkEligibility, getAssessment, getAttempt,
  submitMCQ, submitCode, submitProject,
} from "../services/operations/assessmentAPI"
import CertificateCard from "../components/core/Certificate/CertificateCard"

const STEP = { LOADING: 0, INELIGIBLE: 1, LANDING: 2, MCQ: 3, CODE: 4, PROJECT: 5, RESULT: 6 }

function ScoreBar({ label, score, color = "bg-caribbeangreen-300" }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs text-richblack-300">
        <span>{label}</span>
        <span className="font-semibold text-richblack-5">{score ?? "—"}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-richblack-600">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${score ?? 0}%` }} />
      </div>
    </div>
  )
}

function StepBadge({ label, icon: Icon, active, done }) {
  return (
    <div className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${
      done   ? "bg-caribbeangreen-300/15 text-caribbeangreen-300" :
      active ? "bg-yellow-50/10 text-yellow-50 ring-1 ring-yellow-50/30" :
               "text-richblack-400"
    }`}>
      {done ? <FiCheckCircle size={15} /> : <Icon size={15} />}
      {label}
    </div>
  )
}

export default function Assessment() {
  const { courseId } = useParams()
  const navigate     = useNavigate()
  const { token }    = useSelector((s) => s.auth)
  const { user }     = useSelector((s) => s.profile)

  const [step,        setStep]        = useState(STEP.LOADING)
  const [eligData,    setEligData]    = useState(null)
  const [assessment,  setAssessment]  = useState(null)
  const [attempt,     setAttempt]     = useState(null)
  const [certificate, setCertificate] = useState(null)
  const [busy,        setBusy]        = useState(false)
  const [error,       setError]       = useState("")

  // MCQ state
  const [answers,   setAnswers]   = useState({})   // { questionIndex: selectedOptionIndex }
  const [timeLeft,  setTimeLeft]  = useState(0)
  const timerRef = useRef(null)

  // Code state
  const [code, setCode] = useState("")

  // Project state
  const [projectUrl,  setProjectUrl]  = useState("")
  const [projectDesc, setProjectDesc] = useState("")

  // ── init ──────────────────────────────────────────────────────────────────
  const init = useCallback(async () => {
    setStep(STEP.LOADING)
    setError("")

    // Check for existing certificate / in-progress attempt
    const attemptRes = await getAttempt(courseId, token)
    if (attemptRes?.data?.certificate) {
      setCertificate(attemptRes.data.certificate)
      setAttempt(attemptRes.data.attempt)
      setStep(STEP.RESULT)
      return
    }
    if (attemptRes?.data?.attempt) {
      const a = attemptRes.data.attempt
      setAttempt(a)
      if (a.status === "failed") {
        setEligData({ eligible: false, reason: "cooldown", retryAfter: a.retryAfter })
        setStep(STEP.INELIGIBLE)
        return
      }
      // Resume in-progress attempt
      const asmRes = await getAssessment(courseId, token)
      if (asmRes?.success) {
        setAssessment(asmRes.data)
        if (!a.mcqScore && a.mcqScore !== 0) { setStep(STEP.MCQ); startTimer(asmRes.data.timeLimit); return }
        if (!a.codeScore && a.codeScore !== 0) { setStep(STEP.CODE); return }
        if (!a.projectScore && a.projectScore !== 0) { setStep(STEP.PROJECT); return }
      }
    }

    // Fresh eligibility check
    const elig = await checkEligibility(courseId, token)
    setEligData(elig)
    if (!elig?.eligible) { setStep(STEP.INELIGIBLE); return }

    const asmRes = await getAssessment(courseId, token)
    if (asmRes?.success) setAssessment(asmRes.data)
    setStep(STEP.LANDING)
  }, [courseId, token])

  useEffect(() => { init() }, [init])

  // ── timer ─────────────────────────────────────────────────────────────────
  const startTimer = (minutes) => {
    clearInterval(timerRef.current)
    setTimeLeft(minutes * 60)
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); handleSubmitMCQ(true); return 0 }
        return prev - 1
      })
    }, 1000)
  }
  useEffect(() => () => clearInterval(timerRef.current), [])

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`
  const timerColor = timeLeft < 120 ? "text-pink-400" : timeLeft < 300 ? "text-yellow-50" : "text-caribbeangreen-300"

  // ── submit MCQ ────────────────────────────────────────────────────────────
  const handleSubmitMCQ = useCallback(async (autoSubmit = false) => {
    clearInterval(timerRef.current)
    setBusy(true)
    const answersArr = Object.entries(answers).map(([qi, sel]) => ({
      questionIndex: Number(qi), selected: sel,
    }))
    const res = await submitMCQ(courseId, answersArr, token)
    setBusy(false)
    if (res?.success) {
      setAttempt(res.data.attempt)
      setStep(STEP.CODE)
    } else {
      setError(res?.message || "Failed to submit quiz")
    }
  }, [answers, courseId, token])

  // ── submit code ───────────────────────────────────────────────────────────
  const handleSubmitCode = async () => {
    setBusy(true)
    const res = await submitCode(courseId, code, token)
    setBusy(false)
    if (res?.success) { setAttempt(res.data.attempt); setStep(STEP.PROJECT) }
    else setError(res?.message || "Failed to submit code")
  }

  // ── submit project ────────────────────────────────────────────────────────
  const handleSubmitProject = async () => {
    if (!projectUrl.trim()) { setError("Please enter your project URL"); return }
    setBusy(true)
    const res = await submitProject(courseId, { projectUrl, projectDescription: projectDesc }, token)
    setBusy(false)
    if (res?.success) {
      setAttempt(res.data.attempt)
      if (res.data.certificate) setCertificate(res.data.certificate)
      setStep(STEP.RESULT)
    } else {
      setError(res?.message || "Failed to submit project")
    }
  }

  // ── render ────────────────────────────────────────────────────────────────
  if (step === STEP.LOADING) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-yellow-50 border-t-transparent" />
      </div>
    )
  }

  // ── Ineligible ────────────────────────────────────────────────────────────
  if (step === STEP.INELIGIBLE) {
    const reasonMap = {
      not_enrolled:     { icon: FiXCircle,    color: "text-pink-400",            msg: "You are not enrolled in this course." },
      already_certified:{ icon: FiAward,      color: "text-caribbeangreen-300",  msg: "You already have a certificate for this course!" },
      cooldown:         { icon: FiClock,      color: "text-yellow-50",           msg: `Retry available after ${new Date(eligData?.retryAfter).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` },
      incomplete:       { icon: FiAlertCircle,color: "text-yellow-50",           msg: `Complete at least 80% of the course. You are at ${eligData?.completionPercent ?? 0}%.` },
      no_assessment:    { icon: FiAlertCircle,color: "text-richblack-400",       msg: "Assessment has not been configured for this course yet." },
    }
    const info = reasonMap[eligData?.reason] ?? { icon: FiAlertCircle, color: "text-richblack-400", msg: "You are not eligible to take the assessment right now." }
    const Icon = info.icon

    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <Icon size={48} className={`mx-auto mb-4 ${info.color}`} />
        <h2 className="mb-2 text-xl font-bold text-richblack-5">Assessment Unavailable</h2>
        <p className="mb-8 text-sm text-richblack-300">{info.msg}</p>
        {eligData?.reason === "already_certified" && (
          <button onClick={() => setStep(STEP.RESULT)} className="rounded-lg bg-yellow-50 px-5 py-2 text-sm font-semibold text-richblack-900">
            View Certificate
          </button>
        )}
        <div className="mt-4">
          <Link to="/dashboard/enrolled-courses" className="text-sm text-richblack-400 hover:text-richblack-100">
            ← Back to Courses
          </Link>
        </div>
      </div>
    )
  }

  // ── Landing ───────────────────────────────────────────────────────────────
  if (step === STEP.LANDING) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <Link to="/dashboard/enrolled-courses" className="mb-6 flex items-center gap-1.5 text-sm text-richblack-400 hover:text-richblack-100">
          <FiArrowLeft size={14} /> Back to Courses
        </Link>
        <div className="mb-8">
          <div className="mb-2 flex items-center gap-2">
            <RiSparklingLine className="text-yellow-50" size={20} />
            <span className="text-xs font-semibold uppercase tracking-wider text-yellow-50">Certification Assessment</span>
          </div>
          <h1 className="text-3xl font-bold text-richblack-5">Ready to get certified?</h1>
          <p className="mt-2 text-richblack-300">
            Complete all 3 sections to earn your KodeVedic certificate. Passing score is{" "}
            <span className="font-semibold text-richblack-5">{assessment?.passingScore ?? 70}%</span>.
          </p>
        </div>

        {/* 3-section overview */}
        <div className="mb-8 grid gap-4 sm:grid-cols-3">
          {[
            { icon: FiFileText, label: "MCQ Quiz",      sub: `${assessment?.mcqCount ?? 0} questions · ${assessment?.timeLimit ?? 30} min`, weight: assessment?.weights?.mcq },
            { icon: FiCode,     label: "Code Challenge", sub: "Live in-browser",  weight: assessment?.weights?.code },
            { icon: FiAward,    label: "Project Review", sub: "GitHub / URL",     weight: assessment?.weights?.project },
          ].map(({ icon: Icon, label, sub, weight }) => (
            <div key={label} className="rounded-2xl border border-richblack-700 bg-richblack-800 p-5">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-richblack-700">
                <Icon size={18} className="text-yellow-50" />
              </div>
              <p className="font-semibold text-richblack-5">{label}</p>
              <p className="mt-0.5 text-xs text-richblack-400">{sub}</p>
              <p className="mt-2 text-xs font-medium text-caribbeangreen-300">{weight}% weight</p>
            </div>
          ))}
        </div>

        {/* Rules */}
        <div className="mb-8 rounded-2xl border border-richblack-700 bg-richblack-800 p-5 text-sm text-richblack-300">
          <p className="mb-2 font-semibold text-richblack-5">Before you begin</p>
          <ul className="list-inside list-disc space-y-1">
            <li>You cannot pause or restart once you begin the MCQ timer.</li>
            <li>All 3 sections must be submitted to calculate your score.</li>
            <li>A score of {assessment?.passingScore ?? 70}%+ issues your certificate immediately.</li>
            <li>If you fail, you may retry after 7 days with a gap analysis.</li>
          </ul>
        </div>

        <button
          onClick={() => { setStep(STEP.MCQ); startTimer(assessment?.timeLimit ?? 30) }}
          className="w-full rounded-xl bg-yellow-50 py-3 text-base font-bold text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95"
        >
          Start Assessment
        </button>
      </div>
    )
  }

  // ── MCQ ───────────────────────────────────────────────────────────────────
  if (step === STEP.MCQ) {
    const questions = assessment?.mcqQuestions ?? []
    const answered  = Object.keys(answers).length
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        {/* sticky header */}
        <div className="sticky top-0 z-10 mb-6 flex items-center justify-between rounded-2xl border border-richblack-700 bg-richblack-900/90 px-5 py-3 backdrop-blur">
          <div className="flex items-center gap-3">
            <StepBadge label="MCQ Quiz"  icon={FiFileText} active />
            <FiChevronRight size={14} className="text-richblack-600" />
            <StepBadge label="Code"     icon={FiCode} />
            <FiChevronRight size={14} className="text-richblack-600" />
            <StepBadge label="Project"  icon={FiAward} />
          </div>
          <div className={`flex items-center gap-1.5 font-mono text-sm font-bold ${timerColor}`}>
            <FiClock size={14} /> {fmtTime(timeLeft)}
          </div>
        </div>

        <p className="mb-4 text-xs text-richblack-400">{answered} / {questions.length} answered</p>

        <div className="space-y-6">
          {questions.map((q, qi) => (
            <div key={qi} className={`rounded-2xl border p-5 transition-all ${answers[qi] != null ? "border-richblack-600 bg-richblack-800" : "border-richblack-700 bg-richblack-800"}`}>
              <p className="mb-4 text-sm font-medium text-richblack-5">
                <span className="mr-2 text-richblack-400">{qi + 1}.</span>{q.question}
              </p>
              <div className="space-y-2">
                {q.options.map((opt, oi) => (
                  <button
                    key={oi}
                    onClick={() => setAnswers((prev) => ({ ...prev, [qi]: oi }))}
                    className={`w-full rounded-xl border px-4 py-2.5 text-left text-sm transition-all ${
                      answers[qi] === oi
                        ? "border-yellow-50 bg-yellow-50/10 text-yellow-50"
                        : "border-richblack-600 text-richblack-200 hover:border-richblack-500 hover:bg-richblack-700"
                    }`}
                  >
                    <span className="mr-2 text-richblack-500">{String.fromCharCode(65 + oi)}.</span>{opt}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {error && <p className="mt-4 text-sm text-pink-400">{error}</p>}

        <div className="mt-8">
          <button
            onClick={() => handleSubmitMCQ()}
            disabled={busy}
            className="w-full rounded-xl bg-yellow-50 py-3 font-bold text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95 disabled:opacity-50"
          >
            {busy ? "Submitting…" : `Submit Quiz (${answered}/${questions.length} answered)`}
          </button>
          <p className="mt-2 text-center text-xs text-richblack-500">Unanswered questions count as incorrect.</p>
        </div>
      </div>
    )
  }

  // ── Code Challenge ────────────────────────────────────────────────────────
  if (step === STEP.CODE) {
    const prob = assessment?.codeProblem
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="sticky top-0 z-10 mb-6 flex items-center gap-3 rounded-2xl border border-richblack-700 bg-richblack-900/90 px-5 py-3 backdrop-blur">
          <StepBadge label="MCQ Quiz"  icon={FiFileText} done />
          <FiChevronRight size={14} className="text-richblack-600" />
          <StepBadge label="Code"     icon={FiCode} active />
          <FiChevronRight size={14} className="text-richblack-600" />
          <StepBadge label="Project"  icon={FiAward} />
        </div>

        <div className="mb-6 rounded-2xl border border-richblack-700 bg-richblack-800 p-6">
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-caribbeangreen-300">
            <FiCode size={12} /> Code Challenge
          </div>
          <h2 className="mb-3 text-xl font-bold text-richblack-5">
            {prob?.title || "Solve the problem below"}
          </h2>
          <p className="whitespace-pre-line text-sm leading-relaxed text-richblack-200">
            {prob?.description || "Write a function that solves the given problem. Demonstrate your understanding of the concepts covered in this course."}
          </p>
        </div>

        <div className="mb-4 overflow-hidden rounded-2xl border border-richblack-700">
          <div className="flex items-center justify-between border-b border-richblack-700 bg-richblack-800 px-4 py-2">
            <span className="text-xs text-richblack-400">{prob?.language || "javascript"}</span>
            <button onClick={() => setCode(prob?.starterCode || "")} className="text-xs text-richblack-500 hover:text-richblack-200">
              Reset
            </button>
          </div>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={prob?.starterCode || "// Write your solution here…"}
            className="h-64 w-full resize-none bg-richblack-900 p-4 font-mono text-sm text-richblack-100 focus:outline-none"
            spellCheck={false}
          />
        </div>

        <div className="mb-6 rounded-xl border border-yellow-50/20 bg-yellow-50/5 p-4 text-xs text-yellow-50">
          <strong>Note:</strong> Your code is stored and reviewed. Auto-grading runs when the assessment engine is connected. A placeholder score is applied now.
        </div>

        {error && <p className="mb-4 text-sm text-pink-400">{error}</p>}

        <button
          onClick={handleSubmitCode}
          disabled={busy}
          className="w-full rounded-xl bg-yellow-50 py-3 font-bold text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95 disabled:opacity-50"
        >
          {busy ? "Submitting…" : "Submit Code & Continue"}
        </button>
      </div>
    )
  }

  // ── Project Review ────────────────────────────────────────────────────────
  if (step === STEP.PROJECT) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8">
        <div className="sticky top-0 z-10 mb-6 flex items-center gap-3 rounded-2xl border border-richblack-700 bg-richblack-900/90 px-5 py-3 backdrop-blur">
          <StepBadge label="MCQ Quiz" icon={FiFileText} done />
          <FiChevronRight size={14} className="text-richblack-600" />
          <StepBadge label="Code"    icon={FiCode} done />
          <FiChevronRight size={14} className="text-richblack-600" />
          <StepBadge label="Project" icon={FiAward} active />
        </div>

        <div className="mb-6 rounded-2xl border border-richblack-700 bg-richblack-800 p-6">
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-caribbeangreen-300">
            <FiAward size={12} /> Project Review
          </div>
          <h2 className="mb-3 text-xl font-bold text-richblack-5">Submit your project</h2>
          <p className="text-sm leading-relaxed text-richblack-200">
            {assessment?.projectInstructions || "Share a project you built using the skills from this course. Provide a GitHub or hosted URL along with a short description of what you built."}
          </p>
        </div>

        <div className="mb-4 space-y-4">
          <div className="flex flex-col gap-2">
            <label className="lable-style">Project URL <span className="text-pink-200">*</span></label>
            <input
              type="url"
              value={projectUrl}
              onChange={(e) => { setProjectUrl(e.target.value); setError("") }}
              placeholder="https://github.com/username/project"
              className="form-style"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="lable-style">Description</label>
            <textarea
              rows={4}
              value={projectDesc}
              onChange={(e) => setProjectDesc(e.target.value)}
              placeholder="Briefly describe what you built and what you learned…"
              className="form-style resize-none"
            />
          </div>
        </div>

        {error && <p className="mb-4 text-sm text-pink-400">{error}</p>}

        <button
          onClick={handleSubmitProject}
          disabled={busy}
          className="w-full rounded-xl bg-yellow-50 py-3 font-bold text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95 disabled:opacity-50"
        >
          {busy ? "Submitting…" : "Submit Project & See Results"}
        </button>
      </div>
    )
  }

  // ── Result ────────────────────────────────────────────────────────────────
  if (step === STEP.RESULT) {
    const passed = attempt?.status === "passed" || !!certificate
    const score  = attempt?.totalScore ?? certificate?.totalScore

    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        {/* Pass / Fail banner */}
        <div className={`mb-8 flex flex-col items-center rounded-2xl border p-8 text-center ${
          passed
            ? "border-caribbeangreen-300/30 bg-caribbeangreen-300/5"
            : "border-pink-400/30 bg-pink-400/5"
        }`}>
          {passed
            ? <FiCheckCircle size={48} className="mb-3 text-caribbeangreen-300" />
            : <FiXCircle     size={48} className="mb-3 text-pink-400" />
          }
          <h2 className="text-2xl font-bold text-richblack-5">
            {passed ? "Congratulations! You passed 🎉" : "Not quite there yet"}
          </h2>
          <p className="mt-1 text-richblack-300">
            {passed
              ? "Your certificate has been issued. Download or share it below."
              : `Your score was ${score}%. You need ${attempt?.assessment?.passingScore ?? 70}% to pass.`
            }
          </p>
        </div>

        {/* Score breakdown */}
        <div className="mb-8 rounded-2xl border border-richblack-700 bg-richblack-800 p-6">
          <p className="mb-4 text-sm font-semibold uppercase tracking-wide text-richblack-400">Score Breakdown</p>
          <div className="space-y-4">
            <ScoreBar label="MCQ Quiz (25%)"        score={attempt?.mcqScore     ?? certificate?.mcqScore} />
            <ScoreBar label="Code Challenge (50%)"  score={attempt?.codeScore    ?? certificate?.codeScore} />
            <ScoreBar label="Project Review (25%)"  score={attempt?.projectScore ?? certificate?.projectScore} />
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-richblack-700 pt-4">
            <span className="text-sm font-semibold text-richblack-200">Total Score</span>
            <span className={`text-xl font-bold ${passed ? "text-caribbeangreen-300" : "text-pink-400"}`}>
              {score}%
            </span>
          </div>
        </div>

        {/* Gap analysis on fail */}
        {!passed && attempt?.gapTopics?.length > 0 && (
          <div className="mb-8 rounded-2xl border border-yellow-50/20 bg-yellow-50/5 p-5">
            <p className="mb-3 text-sm font-semibold text-yellow-50">Study these topics before retrying</p>
            <div className="flex flex-wrap gap-2">
              {attempt.gapTopics.map((t, i) => (
                <span key={i} className="rounded-full bg-richblack-700 px-3 py-1 text-xs text-richblack-200">{t}</span>
              ))}
            </div>
            {attempt.retryAfter && (
              <p className="mt-3 text-xs text-richblack-400">
                Retry available after{" "}
                <span className="font-medium text-richblack-200">
                  {new Date(attempt.retryAfter).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              </p>
            )}
          </div>
        )}

        {/* Certificate card */}
        {passed && certificate && (
          <CertificateCard
            certificate={certificate}
            studentName={`${user?.firstName ?? certificate?.student?.firstName ?? ""} ${user?.lastName ?? certificate?.student?.lastName ?? ""}`.trim()}
          />
        )}

        <div className="mt-6 text-center">
          <Link to="/dashboard/enrolled-courses" className="text-sm text-richblack-400 hover:text-richblack-100">
            ← Back to Courses
          </Link>
        </div>
      </div>
    )
  }

  return null
}
